import React from 'react';
import Layout from '../layout/Layout';
import { AppProps } from '../types/pages/index';
import Link from 'next/link';
import { seo } from '../staticData/Seo';
import axios from 'axios';
import Slider from 'react-slick';
import Testimonial from '../components/shared/Testimonial';
import { MainLinkButton } from '../components/styled/buttons/MainLinkButton';
import errorRedirectHandler from '../helpers/errorRedirectHandler';

const TestimonialsPage: React.FC<any> = (props) => {
  const { content, featured, caseStudies } = props;
  const headContent = {
    title: `ActionVFX Testimonials & Reviews`,
    keywords: seo.keywords,
    description:
      "Don't just take our word for it. Read testimonials from satisfied customers and industry professionals who love ActionVFX.",
    og: {
      title: seo.og.title,
      description:
        "Don't just take our word for it. Read testimonials from satisfied customers and industry professionals who love ActionVFX.",
      image: seo.og.image
    }
  };

  const midpoint = Math.ceil(featured.length / 2);
  const firstHalf = featured.slice(0, midpoint);
  const secondHalf = featured.slice(-midpoint);

  const sliderProps = {
    className: 'w-1/2',
    arrows: false,
    infinite: true,
    autoplay: true,
    centerPadding: '60px',
    slidesToShow: 2,
    speed: 2000,
    autoPlaySpeed: 10,
    vertical: true,
    verticalSwiping: true,
    responsive: [
      {
        breakpoint: 1500,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1
        }
      }
    ]
  };

  return (
    <Layout head={headContent}>
      <div className="testimonials-page text-white">
        <section className="bg-gray-900">
          <div className="bg-cover bg-center bg-[url(/img/testimonials.png)]">
            <div className="max-w-[1920px] mx-auto h-[610px] px-12 w-full flex relative">
              <div className="md:w-2/3 flex justify-center flex-col max-h-[700px]">
                <h2 className="uppercase text-[28px] md:text-[36px] lg:text-[40px] font-semibold leading-snug">
                  Trusted by over <span className="text-a-blue">300,000</span> creatives around the
                  globe
                </h2>
                <div
                  className="text-[16px] md:text-[21px] inter max-w-[800px] leading-snug mt-6 text-white-60"
                  dangerouslySetInnerHTML={{ __html: content.text }}
                ></div>
                <img src="/img/partners.png" alt="actionvfx partners" className="w-[500px] mt-12" />
              </div>
              <div className="hidden md:w-1/3 md:flex h-[700px] overflow-hidden">
                <Slider {...sliderProps} rtl={true}>
                  {firstHalf.map((item, key) => (
                    <div key={key} className="pr-3 outline-0">
                      <img
                        className="rounded-[5px] max-h-[441px]"
                        src={item.content.photo.url}
                        alt={item.content.title}
                      />
                    </div>
                  ))}
                </Slider>
                <Slider {...sliderProps} rtl={false}>
                  {secondHalf.map((item, key) => (
                    <div key={key} className="pr-3 outline-0">
                      <img
                        className="rounded-[5px] max-h-[441px]"
                        src={item.content.photo.url}
                        alt={item.content.title}
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 w-full flex flex-col items-center justify-center relative bg-cover bg-center">
          <div className="mt-24 text-center xl:max-w-[1600px] mx-auto">
            <h2 className="blue-bold leading-none text-white uppercase leading-thin text-[28px] md:text-[36px] lg:text-[48px] mx-auto font-semibold tracking-wide">
              Success Stories
            </h2>

            <div className="w-full p-8 md:p-24 flex flex-col md:flex-row gap-16 mt-24 bg-gray-900">
              <div className="w-full md:w-1/2 bg-a-blue rounded-[5px]">
                <Testimonial
                  quote="If you're not using ActionVFX, you're just doing it wrong. It helps us out in every single project, almost every single day. It's hard to find a very specific time where it's really saved us or shined, because it shines constantly."
                  author={null}
                  image={`https://img.youtube.com/vi/8pdx3QOkUmI/hqdefault.jpg`}
                  title={null}
                  video_link={'8pdx3QOkUmI'}
                />
              </div>
              <article className="w-full md:w-1/2 text-left">
                <img
                  src="/img/crafty-apes.png"
                  alt="crafty apes, actionvfx customer"
                  className="w-[120px]"
                />
                <h1 className="inter text-[36px] font-bold leading-tight mt-4">
                  Customer Project: How Crafty Apes VFX Studio Uses ActionVFX Stock Footage
                </h1>
                <p className="text-gray-600 text-[20px] mt-4">
                  Learn how Crafty Apes, a visual effects and production studio, uses the ActionVFX
                  library to gain a competitive edge and produce Hollywood VFX.
                </p>

                <div className="mt-6 flex items-center justify-center md:justify-start">
                  <MainLinkButton
                    href="/blog/how-crafty-apes-use-actionvfx-products-to-gain-a-competitive-edge"
                    inline={true}
                    variant="outlined"
                  >
                    Learn More
                  </MainLinkButton>
                </div>
              </article>
            </div>

            <div className="w-full p-8 md:p-24 flex flex-col md:flex-row gap-16 mt-0 bg-gray-900">
              <article className="w-full md:w-1/2 text-left">
                <img
                  src="/img/vfx-legion.png"
                  alt="vfx legion, actionvfx customer"
                  className="w-[120px]"
                />
                <h1 className="inter text-[36px] font-bold leading-tight mt-4">
                  VFX Legion: How ActionVFX Stock Footage Benefit This VFX Studio
                </h1>
                <p className="text-gray-600 text-[20px] mt-4">
                  Searching for quality VFX assets can be time consuming, and make a VFX shot even
                  more difficult. See why VFX Legion made ActionVFX their one-stop-shop for Fire
                  VFX.
                </p>
                <div className="mt-6 mb-20 md:mb-0 flex items-center justify-center md:justify-start">
                  <MainLinkButton
                    href="/blog/vfx-legion-how-actionvfx-stock-footage-benefit-this-vfx-studio"
                    inline={true}
                    variant="outlined"
                  >
                    Learn More
                  </MainLinkButton>
                </div>
              </article>
              <div className="w-full md:w-1/2 bg-a-blue rounded-[5px]">
                <Testimonial
                  quote="ActionVFX has opened up a lot of opportunities for us to pitch to clients. This material works at all levels from beginner and hobbyist to Hollywood blockbuster and everything in between. Having access to it has been a great tool to showcase what is possible with these elements."
                  author={null}
                  image={`https://img.youtube.com/vi/4Qb3YWlfNfk/hqdefault.jpg`}
                  title={null}
                  video_link={'4Qb3YWlfNfk'}
                />
              </div>
            </div>
          </div>

          <div className="pb-12 px-12 w-full relative">
            <Slider
              {...{
                className: 'w-full mt-12',
                autoplay: true,
                centerPadding: '60px',
                slidesToShow: 3,
                speed: 3000,
                swipeToSlide: true,
                responsive: [
                  {
                    breakpoint: 1500,
                    settings: {
                      slidesToShow: 3
                    }
                  },
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 2
                    }
                  },
                  {
                    breakpoint: 640,
                    settings: {
                      slidesToShow: 1
                    }
                  }
                ]
              }}
            >
              {caseStudies.map((study) => (
                <div className="px-2">
                  <article
                    className="w-full text-left bg-center bg-cover bg-gray-800 rounded-[5px] relative"
                    style={{
                      backgroundImage: `url(${study.header_photo.url})`
                    }}
                  >
                    <div className="w-full bg-black opacity-50 h-full absolute z-1"></div>
                    <div className="flex flex-col justify-end p-4 h-full min-h-[400px] relative z-20">
                      <div className="px-2 md:px-8">
                        <h1 className="inter text-[18px] md:text-[28px] font-bold leading-tight mt-4">
                          {study.title}
                        </h1>
                        <div className="my-6">
                          <MainLinkButton
                            href={`/blog/${study.slug}`}
                            inline={true}
                            variant="outlined"
                            size="auto"
                            className="!h-[56px]"
                          >
                            Learn More
                          </MainLinkButton>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              ))}
            </Slider>
          </div>
        </section>

        <section className="bg-gray-900">
          <div className="py-24 px-12 w-full relative bg-cover bg-center bg-[url(/img/testimonials.png)]">
            <h2 className="text-center blue-bold leading-none text-white uppercase leading-thin text-[28px] md:text-[36px] lg:text-[48px] mx-auto font-semibold tracking-wide">
              Reviews
            </h2>
            <Slider
              {...{
                className: 'w-full mt-12',
                autoplay: true,
                centerPadding: '60px',
                slidesToShow: 3,
                speed: 1000,
                swipeToSlide: true,
                responsive: [
                  {
                    breakpoint: 1500,
                    settings: {
                      slidesToShow: 2
                    }
                  },
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 2
                    }
                  },
                  {
                    breakpoint: 640,
                    settings: {
                      slidesToShow: 1
                    }
                  }
                ]
              }}
            >
              {content.testimonial.structure_items.map((study) => (
                <div className="px-2">
                  <div
                    className="bg-gray-800 rounded-[5px] pt-2 pb-8 px-6 max-h-[400px] overflow-scroll noscrollbar shadow-md"
                    key={study.id}
                  >
                    <p className="text-a-blue text-[82px] leading-none eurostile h-[50px]">“</p>
                    <div
                      className="italic text-[14px] md:text-[16px] lg:text-[24px]"
                      dangerouslySetInnerHTML={{ __html: study.content.quote }}
                    ></div>
                    <div className="flex items-center gap-6 mt-3">
                      <div className="w-[65px] h-[65px] rounded-full flex items-center justify-center overflow-hidden">
                        <img className="" src={study.content?.photo?.url} />
                      </div>
                      <div className="flex flex-col justify-center text-left">
                        <p className="text-white text-[12px] md:text-[16px] font-medium">
                          {study.content.name}
                        </p>
                        <p className="text-[10px] md:text-[14px] font-light text-gray-500">
                          {study.content.title} at{' '}
                          <a className="text-white" href={study.content.company_website || '#'}>
                            {study.content.company}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </section>

        <section
          style={{
            backgroundImage: `url(/img/footer-bg.jpg)`
          }}
          className="w-full flex flex-col items-center justify-center relative bg-cover bg-center"
        >
          <div className="my-24 mx-12 text-center py-8">
            <h2 className="blue-bold leading-none text-white uppercase leading-thin text-[32px] md:text-[48px] lg:text-[64px] mx-auto font-semibold tracking-wide">
              Ready to join the fun?
            </h2>

            <p className="text-[16px] md:text-[24px] inter max-w-[800px] leading-snug my-6 text-white-60">
              Find out how our product can help you achieve your goals.
            </p>

            <div className="flex items-center justify-center gap-8">
              <MainLinkButton href="/product" inline={true} variant="outlined">
                Learn More
              </MainLinkButton>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export const getServerSideProps = async ({ params, req, res }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=59');

  const cmsApi: string = process.env.CMS_API_URL;
  const cmsApiKey: string = process.env.CMS_API_KEY;

  try {
    var pricingRes = await axios.get(
      `${cmsApi}/pages.json?include_content=true&limit=1&where%5Bname%5D=testimonials`,
      {
        headers: {
          Authorization: `Bearer ${cmsApiKey}`
        }
      }
    );

    var featuredRes = await axios.get(
      `${cmsApi}/pages.json?include_content=true&limit=1&where%5Bname%5D=featured`,
      {
        headers: {
          Authorization: `Bearer ${cmsApiKey}`
        }
      }
    );

    var caseStudiesRes = await axios.get(
      `${cmsApi}/articles.json?order_by=publish_date&desc=true&include_content=true&limit=15&where%5Bcategory%5D=9`,
      {
        headers: {
          Authorization: `Bearer ${cmsApiKey}`
        }
      }
    );

    return {
      props: {
        content: pricingRes?.data?.data[0].content,
        caseStudies: caseStudiesRes?.data?.data,
        featured: featuredRes?.data?.data[0].content.featured_slider.structure_items
      }
    };
  } catch (error) {
    return errorRedirectHandler(error);
  }
};

export default TestimonialsPage;
